import React, { useState, useEffect } from 'react';
import { Typography, Box } from '@mui/material';
import { Formik, Form } from 'formik';
import Textfield from '../FormsUI/Textfield';
import Checkbox from '../FormsUI/Checkbox';
import SnackbarShow from '../SnackbarShow';
import Button from '../FormsUI/Button';
import Select from '../FormsUI/Select';
import { boxStyle, formStyle } from '../../Utils/Constants';
import { userCreateFormValidation } from '../../Utils/UserValidations';
import { useNavigate } from 'react-router-dom';
import { createUserInfo } from '../../API/APICalls';
import { validateLocalPart, validateQuota } from '../../Utils/HelperFunctions';
import PasswordTextField from '../FormsUI/PasswordTextfield';


const UserCreateForm = ({ id, domainDetails }) => {
    const [snackbarMessage, setSnackbarMessage] = useState({ open: false, message: '', severity: '' });
    const [disableCreateBtn, setDisableCreateBtn] = useState(false);
    const navigate = useNavigate();
    const initialUserDetails = {
        first_name: '',
        last_name: '',
        mobile_prefix: 91,
        mobile: '',
        local_part: '',
        active: true,
        domain_id: '',
        allocated_space: 0.1,
        password: '',
    };

    useEffect(() => {
        // check if sesison is valid for every 1 minutes
        const interval = setInterval(() => {
            const expiryTime = window.sessionStorage.getItem('expiry_time');
            if (expiryTime && Date.now() > expiryTime) {
                setSnackbarMessage({ open: true, message: 'Session expired. Please login again.', severity: 'error' });
                window.close();
            }
        }, 6 * 1000);
        return () => clearInterval(interval);
    }, []);
    
    // Function to handle user creation
    const createUser = async (id, user) => {
        try {
            user.password = btoa(user.password);
            const response = await createUserInfo(id, user);
            if (response.status === 200) {
                return response;
            } else {
                throw new Error(`User creation failed. Status code: ${response.status}. Message: ${response.data}`);
            }
        } catch (error) {
            throw error;
        };
    };

    // Function to handle form submission
    const handleSubmitForm = async (user) => {
        setDisableCreateBtn(true);
        try {
            await validateLocalPart(id, user.domain_id, user.local_part);
            await validateQuota(id, user.domain_id, user.allocated_space);

            if (window.confirm('Are you sure you want to create a new user?')) {
                const response = await createUser(id, user);
                setSnackbarMessage({
                    open: true,
                    message: 'User created successfully',
                    severity: 'success'
                });
                navigate(`/mailbox/change/${response.data}`);
            };
        } catch (error) {
            user.password = '';
            let errorMessage = error || 'An unknown error occurred.';
            if (error instanceof Error) {
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                errorMessage = error;
            }
            setSnackbarMessage({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
        } finally{
            setDisableCreateBtn(false)
        };
    };
    const snackbarMessageComp = (snackbarMessage.open)
        ? (<SnackbarShow data={snackbarMessage} setSnackbarMessage={setSnackbarMessage} />)
        : null ;


    return (
        <>
            {snackbarMessageComp}
            <Formik initialValues={initialUserDetails} validationSchema={userCreateFormValidation} onSubmit={handleSubmitForm}>
                <Form style={formStyle}>
                    <Typography variant='h4' >Add mailbox</Typography>
                    <Typography variant='body1' sx={{ alignItems: 'center' }}>Name</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, }}>
                        <Textfield name='first_name' label='First Name' />
                        <Textfield name='last_name' label='Last Name' />
                    </Box>
                    <Typography variant='body1' sx={{ alignItems: 'center' }}>Mobile number</Typography>
                    <Box sx={boxStyle}>
                        <Textfield name='mobile_prefix' label='Mobile Prefix code' type='number' />
                        <Textfield name='mobile' label='Mobile Number' />
                    </Box>
                    <Typography variant='body1' sx={{ alignItems: 'center' }}>Other information</Typography>
                    <label>User part. For example: In email 'xyz@domain.com', 'xyz' is the user/local part</label>
                    <Box sx={boxStyle}>
                        <Textfield name='local_part' label='User part' />
                        {domainDetails.domain_ids && (
                            <Select name='domain_id'
                                label='Select domain name'
                                options={domainDetails.domain_ids}
                                noneRequired={false}
                            />
                        )}
                    </Box>
                    <Box sx={boxStyle}>
                        <Textfield name='allocated_space' label='Allocated space in GB' type='number' />
                        <PasswordTextField name='password' label='Password' type='password' />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
                        <Checkbox name='active' label='Active' />
                    </Box>
                    <Button variant='contained' color='primary' sx={{ width: '35%', margin: '0 auto', marginTop: '1rem' }} type='submit' disabled={disableCreateBtn} >Create</Button>
                </Form>
            </Formik>
        </>
    )
};

export default UserCreateForm;